import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';

/**
 * How a Dell reply code is presented in the ticket card's icon rail.
 *
 * The rail has room for roughly three characters, so the badge shows the short **display** code
 * (`PND`, `CUS`, `CNL3`) and everything else goes in the tooltip. The resolution from whichever
 * half ServiceNow stored — it returns `P05` in `value` and `PND - Parts not dispatched [P05]` in
 * `display_value` — already happened server-side in `api/_dell-reply-codes.ts`; this only decides
 * what to render.
 */
export interface ReplyCodeDisplay {
  /** Short code for the badge, e.g. `PND`. */
  label: string;
  /** Hover text — the fullest description available. */
  tooltip: string;
  /** False when the code isn't one of NSC's 16 pairs, so the UI can style it as unrecognised. */
  known: boolean;
}

/**
 * Build the badge + tooltip for a ticket's reply code, or `null` when it has none (which is most
 * of them — only 2 of 2000 open tickets on nsctest carried a code).
 *
 * Tooltip preference, best first:
 *  1. ServiceNow's composite label — `PND - Parts not dispatched [P05]`. The fullest text, and the
 *     only one present for codes outside the 16 pairs.
 *  2. Our own meaning for the pair — `Part not dispatched`. Covers a code stored as a bare value
 *     with no label attached.
 *  3. The code itself, so the tooltip is never empty.
 */
export function formatReplyCode(ticket: ServiceNowTicket): ReplyCodeDisplay | null {
  const label = (ticket.replyCode ?? '').trim();
  if (!label) return null;

  const tooltip = (ticket.replyCodeDisplay ?? '').trim() || (ticket.replyCodeMeaning ?? '').trim() || label;
  // `replyCodeMeaning` is only set when the resolver matched one of the 16 authoritative pairs.
  const known = Boolean((ticket.replyCodeMeaning ?? '').trim());

  return { label, tooltip, known };
}
